import { action, makeObservable, observable } from 'mobx'
import { ObjectFieldStore } from './ObjectFieldStore'
import { FieldStrategy, FieldType, IFieldDefinition } from './Types/FieldTypeEnum'

/**
 * Поле для таблиц (массивов объектов)
 */
export class TableFieldStore extends ObjectFieldStore {
    rows: ObjectFieldStore[] = []

    constructor(definition: IFieldDefinition, formContext: any, path: string[] = []) {
        super(definition, formContext, path)

        makeObservable(this, {
            rows: observable,
            initializeRows: action,
            syncRowsWithValues: action,
            addRow: action,
            removeRow: action,
            moveRow: action,
        })

        // Инициализируем пустым массивом если значение не является массивом
        if (!Array.isArray(this.value)) {
            this.setValue([])
        }

        this.initializeRows()
    }

    /**
     * Ініціалізувати (або переініціалізувати) рядки таблиці на основі поточного значення.
     * Публічний метод — викликається також із FormStore.setInitialValues(),
     * коли дані API надходять після створення полів.
     */
    initializeRows(): void {
        const currentValue = Array.isArray(this.value) ? this.value : []
        this.rows = currentValue.map((_: any, index: number) => {
            return this.createRowField(index)
        })
        
        // Примітка: обчислювальні поля для початкових рядків реєструються
        // в FormStore.createFields() після створення всіх полів
    }

    /**
     * Синхронізує рядки з поточними значеннями: додає відсутні row stores.
     * Ніколи не видаляє збережені рядки, щоб не стирати додані користувачем дані.
     * Викликається із FormStore.setInitialValues().
     */
    syncRowsWithValues(): void {
        const currentValue = Array.isArray(this.value) ? this.value : []
        const currentRowCount = this.rows.length
        const targetRowCount = currentValue.length

        if (currentRowCount === 0 && targetRowCount > 0) {
            // Initial load: no rows yet — build them all
            this.initializeRows()
        } else if (targetRowCount > currentRowCount) {
            // More data items than row stores — add the missing ones
            for (let i = currentRowCount; i < targetRowCount; i++) {
                this.rows.push(this.createRowField(i))
            }
        }
        // If targetRowCount <= currentRowCount we keep existing stores intact
        // (avoids wiping user-added rows when setInitialValues is called again)
    }

    /**
     * Создать поле для строки таблицы
     */
    private createRowField(index: number): ObjectFieldStore {
        const rowDefinition: IFieldDefinition = {
            name: index.toString(),
            type: FieldType.OBJECT,
            fields: this.definition.fields,
            strategy: FieldStrategy.OBJECT,
        }

        return new ObjectFieldStore(rowDefinition, this.formContext, this.path)
    }

    /**
     * Добавить строку в таблицу
     */
    addRow(data: Record<string, any> = {}): void {
        // Use raw observable value (not toJS snapshot) to get current length safely.
        // This avoids replacing the entire tasks array via setValue, which would
        // create new MobX observable objects for existing rows and can lose values
        // when the snapshot races with lazy computed updates.
        const rawArray = this.formContext.getValue(this.path)
        const currentLength = Array.isArray(rawArray) ? rawArray.length : 0

        // Set only the NEW index — leaves existing rows completely untouched in MobX
        this.formContext.setValue([...this.path, currentLength.toString()], data)

        // Добавляем новое поле для строки
        const newRowField = this.createRowField(currentLength)
        this.rows.push(newRowField)

        // Реєструємо обчислювальні поля для нового рядка
        if (this.formContext.registerTableRowComputedFields) {
            this.formContext.registerTableRowComputedFields(this.definition.name, currentLength)
        }
    }

    /**
     * Удалить строку по индексу
     */
    removeRow(index: number): void {
        // Use raw observable array so item references are preserved in MobX (not toJS'd copies)
        const rawArray = this.formContext.getValue(this.path)
        const currentValue = Array.isArray(rawArray) ? rawArray : []
        if (index >= 0 && index < currentValue.length) {
            // Видаляємо обчислювальні поля перед видаленням рядка
            if (this.formContext.unregisterTableRowComputedFields) {
                this.formContext.unregisterTableRowComputedFields(this.definition.name, index)
            }

            const newValue = [...currentValue].filter((_: any, i: number) => i !== index)
            this.setValue(newValue)

            // Удаляем поле строки и перестраиваем остальные
            this.rows.splice(index, 1)
            this.reindexRows()

            // Перереєструємо обчислювальні поля для всіх рядків після видаленого
            this.reregisterComputedFields(index)
        }
    }

    /**
     * Перереєструє обчислювальні поля після зміни індексів
     */
    private reregisterComputedFields(startIndex: number): void {
        if (!this.formContext.registerTableRowComputedFields) return

        // Перереєструємо обчислювальні поля для всіх рядків після startIndex
        for (let i = startIndex; i < this.rows.length; i++) {
            // Спочатку видаляємо старі (з попереднім індексом)
            if (this.formContext.unregisterTableRowComputedFields) {
                this.formContext.unregisterTableRowComputedFields(this.definition.name, i + 1)
            }
            // Потім реєструємо з новим індексом
            this.formContext.registerTableRowComputedFields(this.definition.name, i)
        }
    }

    /**
     * Переместить строку
     */
    moveRow(fromIndex: number, toIndex: number): void {
        // Use raw observable array so item references are preserved in MobX (not toJS'd copies)
        const rawArray = this.formContext.getValue(this.path)
        const currentValue = Array.isArray(rawArray) ? rawArray : []
        if (
            fromIndex >= 0 &&
            fromIndex < currentValue.length &&
            toIndex >= 0 &&
            toIndex < currentValue.length
        ) {
            const newValue = [...currentValue]
            const item = newValue.splice(fromIndex, 1)[0]
            newValue.splice(toIndex, 0, item)
            this.setValue(newValue)

            // Перемещаем поле строки
            const rowField = this.rows.splice(fromIndex, 1)[0]
            this.rows.splice(toIndex, 0, rowField)
            this.reindexRows()
        }
    }

    /**
     * Переиндексировать строки после изменений
     */
    private reindexRows(): void {
        this.rows.forEach((rowField, index) => {
            // Обновляем путь для поля строки
            rowField.path = [...this.path, index.toString()]
        })
    }

    /**
     * Получить строку по индексу
     */
    getRow(index: number): ObjectFieldStore | undefined {
        return this.rows[index]
    }

    /**
     * Получить данные строки по индексу
     */
    getRowData(index: number): any {
        const currentValue = this.value || []
        return currentValue[index]
    }

    /**
     * Установить данные строки по индексу
     */
    setRowData(index: number, data: Record<string, any>): void {
        const currentValue = this.value || []
        if (index >= 0 && index < currentValue.length) {
            const newValue = [...currentValue]
            newValue[index] = { ...newValue[index], ...data }
            this.setValue(newValue)
        }
    }

    get rowCount(): number {
        return this.rows.length
    }

    /**
     * Валидация таблицы
     */
    validate(): boolean {
        // Для TABLE не валідуємо nestedFields (це шаблон полів),
        // а тільки фактичні рядки
        let isValid = true
        
        // Валідуємо базові правила самого TABLE поля (наприклад, isRequired для масиву)
        if (this.isRequired) {
            const value = this.value
            if (!value || (Array.isArray(value) && value.length === 0)) {
                const errorMessage = `Поле "${this.label}" є обов'язковим для заповнення`
                this.formContext.setError(this.path, errorMessage)
                isValid = false
            } else {
                // Очищуємо помилку якщо валідація пройшла
                this.formContext.setError(this.path, null)
            }
        }

        // Валидируем все строки
        this.rows.forEach((rowField) => {
            if (!rowField.validate()) {
                isValid = false
            }
        })

        return isValid
    }
}
